import { ChevronDownIcon } from '@chakra-ui/icons';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import type { OrganizationPermission } from '@/features/permissions/types';
import { useOrganizations } from '@/hooks/useOrganizations';
import { Role } from '@/types/gql.generated';
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  Text,
  useToast,
} from '@/ui';
import { useRemoveExperienceOrganizationPermission } from '../../../hooks/useRemoveExperienceOrganizationPermission';
import { useSetExperienceOrganizationPermission } from '../../../hooks/useSetExperienceOrganizationPermission';

const RESTRICTED = 'restricted';

type Props = {
  organizationPermissions: OrganizationPermission[];
};

// Google-Docs-style "General Access" control, scoped to the experience's home
// org: experiences belong to an org and can only be shared within that org, so
// the section renders only when the experience is homed in a shared
// (FAMILY/BUSINESS) org the viewer belongs to, and the home org is the only
// share target offered. "Restricted" means no org grant.
export const GeneralAccessSection = ({ organizationPermissions }: Props) => {
  const experience = useEffectiveExperience();
  const { sharedOrganizations } = useOrganizations();
  const { setOrganizationPermission, isPending: isSetting } =
    useSetExperienceOrganizationPermission();
  const {
    removeOrganizationPermission,
    removeOrganizationPermissionAsync,
    isPending: isRemoving,
  } = useRemoveExperienceOrganizationPermission();
  const toast = useToast();

  // Covers every hide case at once: personally-homed, no home org yet, or
  // homed in an org the viewer isn't a member of.
  const homeOrg = sharedOrganizations.find(
    (org) => org.id === experience.organizationId
  );
  if (!homeOrg) {
    return null;
  }

  const isUpdating = isSetting || isRemoving;
  const handleMutationError = () =>
    toast.error('Could not update organization access');

  const currentPermission = organizationPermissions.find(
    (permission) => permission.organizationId === homeOrg.id
  );
  const selectedValue = currentPermission?.organizationId ?? RESTRICTED;
  const selectedRole = currentPermission?.role ?? Role.Viewer;
  const isRestricted = selectedValue === RESTRICTED;

  const handleAccessChange = async (value: string) => {
    if (value === selectedValue) {
      return;
    }

    if (value === RESTRICTED) {
      if (currentPermission) {
        removeOrganizationPermission(currentPermission.organizationId, {
          onError: handleMutationError,
        });
      }
      return;
    }

    // Sweep grants to any other org (stale data from before sharing was
    // scoped to the home org) so the home org holds the only grant. The
    // removals are aggregated — consecutive `mutate` calls would only report
    // the last failure. The home grant proceeds regardless: a leftover stale
    // grant is invisible here and can be swept again next time.
    const removals = await Promise.allSettled(
      organizationPermissions
        .filter((permission) => permission.organizationId !== homeOrg.id)
        .map((permission) =>
          removeOrganizationPermissionAsync(permission.organizationId)
        )
    );
    if (removals.some((removal) => removal.status === 'rejected')) {
      handleMutationError();
    }
    setOrganizationPermission(homeOrg.id, selectedRole, {
      onError: handleMutationError,
    });
  };

  const handleRoleChange = (role: Role) => {
    if (!currentPermission || role === currentPermission.role) {
      return;
    }
    setOrganizationPermission(currentPermission.organizationId, role, {
      onError: handleMutationError,
    });
  };

  return (
    <Flex direction="column">
      <Text fontWeight="medium" mb="3">
        General Access
      </Text>

      <Flex align="center">
        <Flex
          align="center"
          bg="gray.100"
          borderRadius="full"
          boxSize="8"
          color="customgray.textdark"
          flexShrink="0"
          justify="center"
          mr="4"
        >
          <Icon icon={isRestricted ? 'Lock' : 'Building2'} size={4} />
        </Flex>

        <Box flex="1">
          <Menu isLazy placement="bottom-start">
            <MenuButton
              as={Button}
              border="none"
              colorScheme="dark"
              fontWeight="normal"
              h="auto"
              isDisabled={isUpdating}
              ml="-4"
              py="1"
              rightIcon={<ChevronDownIcon height="5" width="5" />}
              variant="ghost"
            >
              {isRestricted ? 'Restricted' : homeOrg.name}
            </MenuButton>
            <Portal>
              <MenuList zIndex="var(--chakra-zIndices-modal)">
                <MenuOptionGroup
                  type="radio"
                  value={selectedValue}
                  onChange={(value) => handleAccessChange(value as string)}
                >
                  <MenuItemOption value={RESTRICTED}>Restricted</MenuItemOption>
                  <MenuItemOption value={homeOrg.id}>
                    {homeOrg.name}
                  </MenuItemOption>
                </MenuOptionGroup>
              </MenuList>
            </Portal>
          </Menu>
          <Text color="customgray.mid" fontSize="sm">
            {isRestricted
              ? 'Only people with access can open this page'
              : `Anyone in ${homeOrg.name} can ${
                  selectedRole === Role.Editor ? 'edit' : 'view'
                } this page`}
          </Text>
        </Box>

        {!isRestricted && (
          <Menu isLazy placement="bottom-end">
            <MenuButton
              as={Button}
              colorScheme="dark"
              fontSize="md"
              isDisabled={isUpdating}
              mr="-4"
              rightIcon={<ChevronDownIcon />}
              variant="ghost"
            >
              {selectedRole === Role.Editor ? 'Editor' : 'Viewer'}
            </MenuButton>
            <Portal>
              <MenuList minW="9rem" zIndex="var(--chakra-zIndices-modal)">
                <MenuOptionGroup
                  type="radio"
                  value={selectedRole}
                  onChange={(value) => handleRoleChange(value as Role)}
                >
                  <MenuItemOption value={Role.Viewer}>Viewer</MenuItemOption>
                  <MenuItemOption value={Role.Editor}>Editor</MenuItemOption>
                </MenuOptionGroup>
              </MenuList>
            </Portal>
          </Menu>
        )}
      </Flex>
    </Flex>
  );
};
